import { createFileRoute } from "@tanstack/react-router";
import { Box, Center, Text } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/DataTable";
import {
  type CommunityPermission,
  communityRolePermissions
} from "@/security/communityRolePermissions.gen";
import { SparkRoleCompactDisplayName, SparkRoleKey } from "@/models/SparkRole";

export const Route = createFileRoute(
  "/settings/community/$communityId/permissions"
)({
  component: PermissionsMatrixComponent,
  loader: () => {
    return {
      crumb: "Permissions"
    };
  }
});

// Get all role keys as an array for iteration
const allRoleKeys = Object.values(SparkRoleKey);

// Get all permission keys and their data
const permissionEntries = Object.entries(communityRolePermissions) as [
  string,
  CommunityPermission
][];

const CheckIcon = () => (
  <Center>
    <IconCheck size={17} />
  </Center>
);

interface PermissionAsRowData {
  permissionDisplayName: string;
  globalAdmin: boolean;
  rolePermissions: Record<string, boolean>;
}

function buildPermissionAsRowData(): PermissionAsRowData[] {
  return permissionEntries.map(([, perm]) => {
    const rolePermissions: Record<string, boolean> = {};
    for (const roleKey of allRoleKeys) {
      rolePermissions[roleKey] = perm.allowedRoles.includes(roleKey);
    }
    return {
      permissionDisplayName: perm.displayName,
      globalAdmin: perm.globalAdminCanDo,
      rolePermissions
    };
  });
}

function buildPermissionAsRowColumns(): ColumnDef<
  PermissionAsRowData,
  unknown
>[] {
  const columns: ColumnDef<PermissionAsRowData, unknown>[] = [
    {
      accessorKey: "permissionDisplayName",
      header: "Permission",
      enableSorting: false
    },
    // Global Admin column first
    {
      accessorKey: "globalAdmin",
      header: "Global Admin",
      enableSorting: false,
      cell: ({ getValue }) => (getValue() ? <CheckIcon /> : null),
      meta: { style: { textAlign: "center" } }
    }
  ];

  // Add a column for each SparkRoleKey
  for (const roleKey of allRoleKeys) {
    columns.push({
      id: roleKey,
      header: SparkRoleCompactDisplayName[roleKey],
      enableSorting: false,
      accessorFn: (row) => row.rolePermissions[roleKey],
      cell: ({ getValue }) => (getValue() ? <CheckIcon /> : null),
      meta: { style: { textAlign: "center" } }
    });
  }

  return columns;
}

function PermissionsMatrixComponent() {
  const permissionAsRowData = buildPermissionAsRowData();
  const permissionAsRowColumns = buildPermissionAsRowColumns();

  return (
    <Box>
      <Text size="sm" c="dimmed" mb="md">
        This reference shows which roles are allowed to perform which actions in
        the app. It is identical for every community and is shown here for
        convenience.
      </Text>
      <DataTable
        data={permissionAsRowData}
        columns={permissionAsRowColumns}
        stickyFirstColumn
      />
    </Box>
  );
}
